import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { finalize, firstValueFrom, from, Observable, of } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import { HotToastService } from '@ngneat/hot-toast';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

import { DemoService } from './../../../../demo.service';
import { LegalDateTimeService } from './../../../../services/legal-datetime.service';
import { GlobalFunctionsService } from './../../../../services/global-functions.service';
import {
  ActivityMetricsV2AgentRollup,
  ActivityMetricsV2BackendMode,
  ActivityMetricsV2BackendDecision,
  ActivityMetricsV2Breakdown,
  ActivityMetricsV2ChunkDiagnostic,
  ActivityMetricsV2BreakdownRow,
  ActivityMetricsV2Diagnostics,
  ActivityMetricsV2Event,
  ActivityMetricsV2ExportResponse,
  ActivityMetricsV2FixedPointAudit,
  ActivityMetricsV2MetricDefinition,
  ActivityMetricsV2MetricAuditRow,
  ActivityMetricsV2MetricContributorRow,
  ActivityMetricsV2MetricEligibilityRow,
  ActivityMetricsV2MetricResult,
  ActivityMetricsV2Response,
  ActivityMetricsV2Search,
  ActivityMetricsV2SourceCount,
  ActivityMetricsV2StoreCleanupResponse,
  ActivityMetricsV2StoreCoverage,
  ActivityMetricsV2StoreEventCount,
  ActivityMetricsV2StoreReportRunStatusCount,
  ActivityMetricsV2StoreSetupResponse,
  ActivityMetricsV2StoreStatus,
  ActivityMetricsV2StoreSyncResponse,
  ActivityMetricsV2StoreSyncRun,
  ActivityMetricsV2StoreSyncState,
  ActivityMetricsV2SubjectRollup,
} from './../../../../shared/models/activity-metrics-v2.model';
import {
  ACTIVITY_METRICS_V2_DEFAULT_GROUP_BY,
  ACTIVITY_METRICS_V2_DEFAULT_SOURCES,
  ACTIVITY_METRICS_V2_INDEXED_TRANSITION_TIMELINE_LIMIT,
  ActivityMetricsV2CoveragePresentation,
  applyActivityMetricsV2IndexedWindowToRequest,
  buildActivityMetricsV2CoveragePresentation,
  getActivityMetricsV2PreviousFullMonthWindow,
  isActivityMetricsV2Operator,
} from './../activity-metrics-v2-indexed-reporting.util';

interface SelectOption {
  text: string;
  value: string | number;
}

interface HelpSection {
  title: string;
  lines: string[];
}

interface HelpContent {
  key: string;
  title: string;
  summary: string;
  sections: HelpSection[];
  future_note?: string;
}

type ActivityMetricsV2ViewKey = 'all' | 'summary' | 'metrics' | 'breakdowns' | 'audit' | 'technical';

@Component({
  selector: 'app-agent-performance-activity-metrics-attempts',
  templateUrl: './agent-performance-activity-metrics-attempts.component.html',
  styleUrls: ['./agent-performance-activity-metrics-attempts.component.scss']
})
export class AgentPerformanceActivityMetricsAttemptsComponent implements OnInit, OnDestroy {
  readonly helpContentMap: Record<string, HelpContent> = {
    backend_mode: {
      key: 'backend_mode',
      title: 'Backend Mode',
      summary: 'Chooses where Metrics 2.0 reads from when building the report.',
      sections: [
        {
          title: 'Options',
          lines: [
            'The main 2.0 page now defaults to `Reporting Tables` so indexed truth is the normal path.',
            '`Auto` uses the reporting/index tables when they are ready and can fall back to live tables.',
            '`Reporting Tables` forces the indexed 2.0 tables.',
            '`Live Tables` forces direct reads from the raw operational tables.'
          ]
        },
        {
          title: 'Why It Exists',
          lines: [
            'This lets you validate parity during rollout instead of cutting over blindly.',
            'Large windows should ultimately run from reporting tables, while legacy and truth checks can still compare against live tables.'
          ]
        }
      ],
      future_note: 'Planned to move into managed help/config metadata later so rollout wording can be updated without changing component code.'
    },
    reporting_tables: {
      key: 'reporting_tables',
      title: 'Reporting Tables, Setup, Sync, And Seed',
      summary: 'These controls manage the derived 2.0 reporting layer in the same database. They do not replace the operational source tables.',
      sections: [
        {
          title: 'Setup Sources',
          lines: [
            'Seeds or refreshes the source-definition metadata so Metrics 2.0 knows which raw sources can feed the reporting layer.',
            'This is metadata setup, not business data creation.'
          ]
        },
        {
          title: 'Sync Current Window',
          lines: [
            'Pulls the selected date window from the operational source tables and indexes it into the reporting tables.',
            'Large windows are indexed in smaller modules so the browser does not need to hold one giant sync request open.',
            'Sync scope is based on the selected window and selected activity sources. Report filters such as subject, agent, state, and outcome do not narrow the indexed dataset.'
          ]
        },
        {
          title: 'Cleanup Controls',
          lines: [
            '`Clean Current Window` removes indexed Metrics 2.0 rows for the selected window and sources, then invalidates overlapping coverage so the window can be rebuilt cleanly.',
            '`Clean All Indexed Data` wipes the derived Metrics 2.0 event-store dataset and resets sync-state coverage, while leaving raw operational tables and run-history logs in place.'
          ]
        },
        {
          title: 'Seed SQL',
          lines: [
            'The seed SQL creates starter definitions, KPI groups, benchmarks, and export-profile rows so the 2.0 system is not an empty skeleton.',
            'Runtime event rows still come from sync and backfill, not dummy inserts.'
          ]
        }
      ],
      future_note: 'Planned admin CRUD: source control, sync monitoring, KPI config, export profiles, and help-content management.'
    },
    projection: {
      key: 'projection',
      title: 'Projection',
      summary: 'Controls whether the report returns the standard UI payload or a more audit-oriented payload.',
      sections: [
        {
          title: 'Options',
          lines: [
            '`UI` is the normal on-screen reporting mode.',
            '`Audit` is intended for deeper export, trace, and validation use.'
          ]
        }
      ]
    },
    timeline_limit: {
      key: 'timeline_limit',
      title: 'Timeline Limit',
      summary: 'Limits how many timeline rows are returned in the response.',
      sections: [
        {
          title: 'Why It Matters',
          lines: [
            'Higher numbers return more rows but increase payload size and load time.',
            'For large windows, this should stay conservative while summary and grouped metrics remain available.',
            'This caps returned timeline rows and UI payload only; it does not change metric counts, fixed-point calculations, or in-window event admission.'
          ]
        }
      ]
    },
    export_profile_key: {
      key: 'export_profile_key',
      title: 'Export Profile',
      summary: 'Chooses the preferred output shape when exporting the current report.',
      sections: [
        {
          title: 'Examples',
          lines: [
            'Timeline Audit focuses on event-level review.',
            'Subject Rollup and Agent Rollup focus on grouped operational results.',
            'Region and Outcome Breakdown focus on KPI grouping outputs.'
          ]
        }
      ]
    },
    when_window: {
      key: 'when_window',
      title: 'Date Window',
      summary: 'This is the Brisbane-legal reporting window used to admit activity into the report.',
      sections: [
        {
          title: 'Rule',
          lines: [
            'Jobs and subjects are included because they have activity inside this window, not just because the job was originally created inside it.',
            'Mixed source dates are normalized before fixed points and metrics are calculated.'
          ]
        }
      ]
    },
    brand_id_group: {
      key: 'brand_id_group',
      title: 'Agents',
      summary: 'Filters the report to selected agent brands or groups.',
      sections: [
        {
          title: 'Use',
          lines: [
            'Use this to scope activity, metrics, and grouped KPI results to specific agent brand records.'
          ]
        }
      ]
    },
    brand_report_id_group: {
      key: 'brand_report_id_group',
      title: 'Reporting Clients',
      summary: 'Filters the report to selected reporting client brands or groups.',
      sections: [
        {
          title: 'Use',
          lines: [
            'Useful for client-specific KPI reviews and export runs.'
          ]
        }
      ]
    },
    subject_number: {
      key: 'subject_number',
      title: 'Subject',
      summary: 'Targets a single known subject number on top of the wider date and source filters.',
      sections: [
        {
          title: 'Use',
          lines: [
            'Best used for validation or audit work when checking one subject against the timeline and fixed-point trace.'
          ]
        }
      ]
    },
    job_id: {
      key: 'job_id',
      title: 'Job',
      summary: 'Targets a single known job id on top of the wider date and source filters.',
      sections: [
        {
          title: 'Use',
          lines: [
            'Best used for exact job-level validation when checking one known job across timeline, rollups, and fixed-point audit.',
            'This does not change indexed coverage rules. It narrows the report after the selected date window and sources are admitted.'
          ]
        }
      ]
    },
    subject_service_type_id_group: {
      key: 'subject_service_type_id_group',
      title: 'Service Types',
      summary: 'Restricts the report to selected subject service types.',
      sections: [
        {
          title: 'KPI Impact',
          lines: [
            'This affects both the timeline and the KPI rollups built from attempts and related activity.'
          ]
        }
      ]
    },
    subject_service_outcome_id_group: {
      key: 'subject_service_outcome_id_group',
      title: 'Exact Outcomes',
      summary: 'Filters the report by the older exact service outcome values from subject_service_outcomes.',
      sections: [
        {
          title: 'Use',
          lines: [
            'Use this when you need the operational exact outcome names rather than the higher-level resolution KPI outcome.',
            'This is complementary to resolution outcome filtering, not a replacement for it.'
          ]
        }
      ]
    },
    subject_service_resolution_outcome_id_group: {
      key: 'subject_service_resolution_outcome_id_group',
      title: 'Resolution Outcomes',
      summary: 'Uses the primary KPI outcome dimension for Metrics 2.0.',
      sections: [
        {
          title: 'Rule',
          lines: [
            'This filter is based on `subject_service_resolution_outcome_id`, not the older exact service outcome field.',
            'It is intended for KPI and benchmark grouping, not just raw legacy status display.'
          ]
        }
      ]
    },
    job_type_id_group: {
      key: 'job_type_id_group',
      title: 'Job Types',
      summary: 'Filters activity to jobs assigned to the selected job types.',
      sections: [
        {
          title: 'Scope',
          lines: [
            'This filter works across all activity sources by resolving the event back to its linked job.',
            'It affects report reads, not the sync/index scope for the selected date window.'
          ]
        }
      ]
    },
    activity_sources: {
      key: 'activity_sources',
      title: 'Activity Sources',
      summary: 'Chooses which raw activity sources are allowed to admit and describe activity in the current report.',
      sections: [
        {
          title: 'Current Sources',
          lines: [
            'System Log, Attempts/Services, Notes, Communications, Files/Documents, Jobs, and Subjects.'
          ]
        },
        {
          title: 'Future Direction',
          lines: [
            'This source list is intended to become fully managed through source-definition metadata and settings.'
          ]
        }
      ]
    },
    group_by: {
      key: 'group_by',
      title: 'Breakdown Groups',
      summary: 'Chooses which grouped KPI summaries are returned with the response.',
      sections: [
        {
          title: 'Examples',
          lines: [
            'Service Type, Resolution Outcome, Resolution Result, Attempt Lifecycle, Subject Lifecycle, State, Postcode, Suburb/Region, and Service Type x Outcome.'
          ]
        }
      ]
    },
    state_group: {
      key: 'state_group',
      title: 'State Filter',
      summary: 'Filters resolved region activity by state values.',
      sections: [
        {
          title: 'Format',
          lines: [
            'Enter one or more state values separated by commas.'
          ]
        }
      ]
    },
    postcode_group: {
      key: 'postcode_group',
      title: 'Postcode Filter',
      summary: 'Filters resolved region activity by postcode.',
      sections: [
        {
          title: 'Format',
          lines: [
            'Enter one or more postcode values separated by commas.'
          ]
        }
      ]
    },
    suburb_group: {
      key: 'suburb_group',
      title: 'Suburb Filter',
      summary: 'Filters resolved region activity by suburb text.',
      sections: [
        {
          title: 'Format',
          lines: [
            'Enter one or more suburb values separated by commas.'
          ]
        }
      ]
    },
    include_breakdowns: {
      key: 'include_breakdowns',
      title: 'Include Breakdown Payloads',
      summary: 'Determines whether grouped KPI breakdown payloads are returned in the report response.',
      sections: [
        {
          title: 'Performance',
          lines: [
            'Turn this off when you want the lighter summary/timeline path and do not need the grouped breakdown blocks immediately.'
          ]
        }
      ]
    }
  };

  readonly sourceOptions: SelectOption[] = [
    { text: 'System Log', value: 'system_log' },
    { text: 'Attempts / Services', value: 'subject_services' },
    { text: 'Notes', value: 'subject_notes' },
    { text: 'Communications', value: 'subject_communication_history' },
    { text: 'Files / Documents', value: 'files' },
    { text: 'Jobs', value: 'jobs' },
    { text: 'Subjects', value: 'subjects' },
  ];

  readonly groupByOptions: SelectOption[] = [
    { text: 'Service Type', value: 'service_type' },
    { text: 'Resolution Outcome', value: 'resolution_outcome' },
    { text: 'Resolution Result', value: 'resolution_result' },
    { text: 'Resolution Score', value: 'resolution_score' },
    { text: 'Attempt Lifecycle', value: 'attempt_lifecycle' },
    { text: 'Subject Lifecycle', value: 'subject_lifecycle' },
    { text: 'State', value: 'state' },
    { text: 'Postcode', value: 'postcode' },
    { text: 'Suburb + Region', value: 'suburb_region' },
    { text: 'Service Type x Outcome', value: 'service_type_x_resolution_outcome' },
  ];

  readonly exportProfileOptions: SelectOption[] = [
    { text: 'Timeline Audit', value: 'timeline_audit' },
    { text: 'Subject Rollup', value: 'subject_rollup' },
    { text: 'Agent Rollup', value: 'agent_rollup' },
    { text: 'Metric Results', value: 'metric_results' },
    { text: 'Region Breakdown', value: 'region_breakdown' },
    { text: 'Outcome Breakdown', value: 'outcome_breakdown' },
    { text: 'Lifecycle Breakdown', value: 'lifecycle_breakdown' },
  ];

  readonly backendModeOptions: Array<{ label: string; value: ActivityMetricsV2BackendMode }> = [
    { label: 'Auto', value: 'auto' },
    { label: 'Reporting Tables', value: 'event_store' },
    { label: 'Live Tables', value: 'live' },
  ];
  readonly monthOptions = [
    { index: 0, label: 'Jan' },
    { index: 1, label: 'Feb' },
    { index: 2, label: 'Mar' },
    { index: 3, label: 'Apr' },
    { index: 4, label: 'May' },
    { index: 5, label: 'Jun' },
    { index: 6, label: 'Jul' },
    { index: 7, label: 'Aug' },
    { index: 8, label: 'Sep' },
    { index: 9, label: 'Oct' },
    { index: 10, label: 'Nov' },
    { index: 11, label: 'Dec' },
  ];

  readonly defaultSources = [...ACTIVITY_METRICS_V2_DEFAULT_SOURCES];
  readonly defaultGroupBy = [...ACTIVITY_METRICS_V2_DEFAULT_GROUP_BY];

  readonly brandFields: Object = { text: 'NameAndAddress', value: 'brand_id' };
  readonly jobTypeFields: Object = { text: 'job_type_name', value: 'job_type_id' };
  readonly serviceTypeFields: Object = { text: 'subject_service_type_name', value: 'subject_service_type_id' };
  readonly exactOutcomeFields: Object = { text: 'service_outcome_name', value: 'subject_service_outcome_id' };
  readonly resolutionOutcomeFields: Object = { text: 'name', value: 'id' };
  readonly sourceFields: Object = { text: 'text', value: 'value' };
  readonly exportFields: Object = { text: 'text', value: 'value' };

  brands_agent: any[] = [];
  brands_client: any[] = [];
  job_types: any[] = [];
  subject_service_types: any[] = [];
  subject_service_outcomes: any[] = [];
  subject_service_resolution_outcomes: any[] = [];
  lookup_warning_messages: string[] = [];

  pickerValues: { when_from: Date | null; when_to: Date | null } = {
    when_from: null,
    when_to: null,
  };
  activeQuickRangeDays: number | null = null;
  selectedCalendarYear = new Date().getFullYear();
  activeMonthIndex: number | null = null;
  activeDayOfMonth: number | null = null;

  search_model: ActivityMetricsV2Search = this.createDefaultSearch();
  response: ActivityMetricsV2Response | null = null;
  export_preview: ActivityMetricsV2ExportResponse | null = null;
  store_status: ActivityMetricsV2StoreStatus | null = null;
  store_setup_result: ActivityMetricsV2StoreSetupResponse | null = null;
  store_sync_result: ActivityMetricsV2StoreSyncResponse | null = null;
  store_cleanup_result: ActivityMetricsV2StoreCleanupResponse | null = null;
  loading = false;
  loading_started_at_ms: number | null = null;
  loading_elapsed_seconds = 0;
  loading_timer: ReturnType<typeof setInterval> | null = null;
  is_exporting = false;
  is_store_status_loading = false;
  is_store_setting_up = false;
  is_store_syncing = false;
  is_store_cleaning = false;
  report_error_message: string | null = null;
  store_error_message: string | null = null;
  store_sync_progress_message: string | null = null;
  store_sync_total_modules = 0;
  store_sync_completed_modules = 0;
  helpModalRef?: BsModalRef;
  auditReviewModalRef?: BsModalRef;
  activeHelpContent: HelpContent | null = null;
  collapsedSections: { [sectionKey: string]: boolean } = {
    returned_detail_rows: true,
    technical_details: true,
    validation_snapshot: false,
    stage_timings: true,
    source_fetch: true,
    chunk_trace: true,
    warnings: false,
    metric_groups: false,
    selected_metric_audit: false,
    unified_timeline: true,
    subject_rollup: true,
    agent_rollup: true,
    audit_drilldown: true,
    export_preview: true,
  };

  selectedTimelineEvent: ActivityMetricsV2Event | null = null;
  selectedSubjectRollup: ActivityMetricsV2SubjectRollup | null = null;
  selectedAuditRecord: ActivityMetricsV2FixedPointAudit | null = null;
  selectedMetricKey: string | null = null;
  activeView: ActivityMetricsV2ViewKey = 'all';
  selectedAttemptId: number | null = null;
  selectedSubjectId: number | null = null;

  constructor(
    private readonly demoService: DemoService,
    public readonly gfunc: GlobalFunctionsService,
    public readonly legalDate: LegalDateTimeService,
    public readonly datepipe: DatePipe,
    public readonly toastr: HotToastService,
    private readonly modalService: BsModalService,
  ) {}

  ngOnInit(): void {
    this.gfunc.setLoginSessionVars();
    this.search_model = this.createDefaultSearch();
    if (!this.isOperatorUser) {
      this.search_model.backend_mode = 'event_store';
    }
    this.syncPickerValues();
    this.loadLookups();
    this.loadStoreStatus();
  }

  ngOnDestroy(): void {
    this.stopLoadingTimer();
  }

  get isOperatorUser(): boolean {
    return isActivityMetricsV2Operator(this.gfunc.sessionUserId);
  }

  get summaryCards(): Array<{ label: string; value: number; helper: string }> {
    if (!this.response) {
      return [];
    }

    const summary = this.response.summary;
    return [
      {
        label: 'Events In Window',
        value: summary.total_events,
        helper: `${summary.source_counts.length} active sources`,
      },
      {
        label: 'Attempts In Window',
        value: summary.total_attempts,
        helper: 'Admitted attempt/service rows for metric evaluation',
      },
      {
        label: 'Subjects With Activity',
        value: summary.total_subjects,
        helper: `${summary.total_jobs} jobs admitted by activity`,
      },
      {
        label: 'Agent Footprint',
        value: summary.total_agents,
        helper: `${summary.total_regions} regions resolved`,
      },
    ];
  }

  get timeline(): ActivityMetricsV2Event[] {
    return this.response?.timeline ?? [];
  }

  get subjects(): ActivityMetricsV2SubjectRollup[] {
    return this.response?.subjects ?? [];
  }

  get agents(): ActivityMetricsV2AgentRollup[] {
    return this.response?.agents ?? [];
  }

  get breakdowns(): ActivityMetricsV2Breakdown[] {
    return this.response?.grouped_breakdowns ?? [];
  }

  get diagnostics(): ActivityMetricsV2Diagnostics | null {
    return this.response?.diagnostics ?? null;
  }

  get backendDecision(): ActivityMetricsV2BackendDecision | null {
    return this.diagnostics?.backend_decision ?? null;
  }

  get backendDecisionAlertClass(): string {
    if (!this.backendDecision) {
      return 'alert alert-info';
    }

    if (!this.backendDecision.can_run) {
      return 'alert alert-danger';
    }

    return this.backendDecision.will_use_event_store ? 'alert alert-success' : 'alert alert-warning';
  }

  get storeSourceCounts(): ActivityMetricsV2StoreEventCount[] {
    return this.store_status?.event_counts?.by_source ?? [];
  }

  get latestStoreSyncRuns(): ActivityMetricsV2StoreSyncRun[] {
    return this.store_status?.latest_sync_runs ?? [];
  }

  get latestStoreSyncRun(): ActivityMetricsV2StoreSyncRun | null {
    return this.latestStoreSyncRuns[0] ?? null;
  }

  get storeSyncState(): ActivityMetricsV2StoreSyncState[] {
    return this.store_status?.sync_state ?? [];
  }

  get storeReportRunStatusCounts(): ActivityMetricsV2StoreReportRunStatusCount[] {
    return this.store_status?.report_run_status_counts ?? [];
  }

  get storeTotalEvents(): number {
    return this.store_status?.event_counts?.total ?? 0;
  }

  get storeCoverage(): ActivityMetricsV2StoreCoverage | null {
    return this.store_status?.coverage ?? null;
  }

  get storeCoveragePresentation() {
    return this.buildCoveragePresentation(this.buildRequestModel(), this.store_status);
  }

  get storeCoverageIndexedThrough(): string | null {
    return this.storeCoveragePresentation.indexedThroughUtc;
  }

  get storeCoverageGapMs(): number | null {
    return this.storeCoveragePresentation.gapMs;
  }

  get storeCoverageIsMovingGap(): boolean {
    return this.storeCoveragePresentation.isMovingGap;
  }

  get storeCoverageAlertClass(): string {
    return this.storeCoveragePresentation.alertClass;
  }

  get storeCoverageHeadline(): string {
    return this.storeCoveragePresentation.headline;
  }

  get storeCoverageDetail(): string {
    return this.storeCoveragePresentation.detail;
  }

  get shouldShowStoreCoverageAction(): boolean {
    return this.isOperatorUser && this.storeCoveragePresentation.showAction;
  }

  get storeCoverageActionLabel(): string {
    return this.storeCoveragePresentation.actionLabel || 'Sync current window';
  }

  get isReportingTablesRequested(): boolean {
    return this.buildRequestModel().backend_mode === 'event_store';
  }

  get diagnosticsSourceRows(): number {
    return (this.diagnostics?.source_diagnostics ?? []).reduce((total, source) => total + (source.row_count ?? 0), 0);
  }

  get diagnosticsWarnings(): string[] {
    return this.diagnostics?.warnings ?? [];
  }

  get diagnosticsChunking() {
    return this.diagnostics?.chunking ?? null;
  }

  get diagnosticsChunkRows(): ActivityMetricsV2ChunkDiagnostic[] {
    return this.diagnosticsChunking?.chunk_diagnostics ?? [];
  }

  get metricGroups(): Array<{ key: string; label: string; description: string | null; metrics: ActivityMetricsV2MetricResult[]; breakdowns: ActivityMetricsV2Breakdown[] }> {
    if (!this.response) {
      return [];
    }

    return (this.response.kpi_groups ?? []).map((group) => ({
      key: group.group_key,
      label: group.label,
      description: group.description ?? null,
      metrics: (group.metric_keys ?? [])
        .map((metricKey) => this.getMetricResult(metricKey))
        .filter((metric): metric is ActivityMetricsV2MetricResult => !!metric),
      breakdowns: (group.breakdown_keys ?? [])
        .map((breakdownKey) => this.getBreakdown(breakdownKey))
        .filter((breakdown): breakdown is ActivityMetricsV2Breakdown => !!breakdown),
    }));
  }

  get singleSubjectMetricAuditMap(): { [metricKey: string]: ActivityMetricsV2MetricAuditRow | undefined } {
    const auditRows = this.response?.fixed_point_results;
    if (!auditRows || auditRows.length !== 1) {
      return {};
    }

    const metricAuditRows = auditRows[0]?.metric_audit ?? [];
    return metricAuditRows.reduce((accumulator, row) => {
      accumulator[row.metric_key] = row;
      return accumulator;
    }, {} as { [metricKey: string]: ActivityMetricsV2MetricAuditRow | undefined });
  }

  get calendarYearOptions(): number[] {
    const currentYear = new Date().getFullYear() + 1;
    const earliestYear = 2020;
    const years: number[] = [];

    for (let year = currentYear; year >= earliestYear; year -= 1) {
      years.push(year);
    }

    return years;
  }

  get selectedCalendarMonthIndex(): number | null {
    if (this.activeMonthIndex !== null) {
      return this.activeMonthIndex;
    }

    const whenFrom = this.pickerValues.when_from;
    const whenTo = this.pickerValues.when_to;
    if (!whenFrom || !whenTo) {
      return null;
    }

    const isSameMonth = whenFrom.getFullYear() === whenTo.getFullYear()
      && whenFrom.getMonth() === whenTo.getMonth();

    return isSameMonth ? whenFrom.getMonth() : null;
  }

  get dayOptions(): number[] {
    const monthIndex = this.selectedCalendarMonthIndex;
    if (monthIndex === null) {
      return [];
    }

    const daysInMonth = new Date(this.selectedCalendarYear, monthIndex + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, index) => index + 1);
  }

  createDefaultSearch(): ActivityMetricsV2Search {
    const defaultWindow = getActivityMetricsV2PreviousFullMonthWindow(this.legalDate.toPickerDate(new Date()) ?? new Date());
    const whenFrom = defaultWindow.from;
    const whenTo = defaultWindow.to;

    return {
      when_from: this.legalDate.fromPickerDate(whenFrom),
      when_to: this.legalDate.fromPickerDate(whenTo),
      limit: ACTIVITY_METRICS_V2_INDEXED_TRANSITION_TIMELINE_LIMIT,
      projection: 'ui',
      backend_mode: 'event_store',
      activity_sources: [...this.defaultSources],
      brand_id_group: [],
      brand_report_id_group: [],
      subject_number_group: [],
      subject_number: null,
      subject_id: null,
      job_id: null,
      job_type_id_group: [],
      subject_service_type_id_group: [],
      subject_service_resolution_outcome_id_group: [],
      subject_service_outcome_id_group: [],
      state_group: [],
      postcode_group: [],
      suburb_group: [],
      group_by: [...this.defaultGroupBy],
      include_breakdowns: false,
      export_profile_key: 'timeline_audit',
      saved_file_name: null,
    };
  }

  syncPickerValues(): void {
    this.pickerValues = {
      when_from: this.legalDate.toPickerDate(this.search_model.when_from),
      when_to: this.legalDate.toPickerDate(this.search_model.when_to),
    };
    this.syncCalendarSelection();
  }

  setQuickRange(days: number): void {
    const whenTo = this.legalDate.toPickerDate(new Date()) ?? new Date();
    const whenFrom = new Date(whenTo.getTime());
    whenFrom.setDate(whenFrom.getDate() - days);

    this.activeQuickRangeDays = days;
    this.pickerValues.when_from = whenFrom;
    this.pickerValues.when_to = whenTo;
    this.search_model.when_from = this.legalDate.fromPickerDate(whenFrom);
    this.search_model.when_to = this.legalDate.fromPickerDate(whenTo);
    this.report_error_message = null;
    this.syncCalendarSelection();
  }

  setMonthWindow(monthIndex: number): void {
    const whenFrom = new Date(this.selectedCalendarYear, monthIndex, 1, 0, 0, 0, 0);
    const whenTo = new Date(this.selectedCalendarYear, monthIndex + 1, 0, 23, 59, 0, 0);

    this.activeQuickRangeDays = null;
    this.activeMonthIndex = monthIndex;
    this.activeDayOfMonth = null;
    this.pickerValues.when_from = whenFrom;
    this.pickerValues.when_to = whenTo;
    this.search_model.when_from = this.legalDate.fromPickerDate(whenFrom);
    this.search_model.when_to = this.legalDate.fromPickerDate(whenTo);
    this.report_error_message = null;
  }

  setDayWindow(dayOfMonth: number): void {
    const monthIndex = this.selectedCalendarMonthIndex;
    if (monthIndex === null) {
      return;
    }

    const whenFrom = new Date(this.selectedCalendarYear, monthIndex, dayOfMonth, 0, 0, 0, 0);
    const whenTo = new Date(this.selectedCalendarYear, monthIndex, dayOfMonth, 23, 59, 0, 0);

    this.activeQuickRangeDays = null;
    this.activeMonthIndex = monthIndex;
    this.activeDayOfMonth = dayOfMonth;
    this.pickerValues.when_from = whenFrom;
    this.pickerValues.when_to = whenTo;
    this.search_model.when_from = this.legalDate.fromPickerDate(whenFrom);
    this.search_model.when_to = this.legalDate.fromPickerDate(whenTo);
    this.report_error_message = null;
  }

  onCalendarYearChange(year: number | string): void {
    const numericYear = Number(year);
    if (!Number.isFinite(numericYear)) {
      return;
    }

    this.selectedCalendarYear = numericYear;
    const monthIndex = this.selectedCalendarMonthIndex;

    if (monthIndex !== null && this.activeDayOfMonth !== null) {
      this.activeMonthIndex = monthIndex;
      const maxDay = new Date(this.selectedCalendarYear, monthIndex + 1, 0).getDate();
      this.setDayWindow(Math.min(this.activeDayOfMonth, maxDay));
      return;
    }

    if (monthIndex !== null) {
      this.setMonthWindow(monthIndex);
    }
  }

  onPickerChange(field: 'when_from' | 'when_to', value: Date | null): void {
    this.activeQuickRangeDays = null;
    this.pickerValues[field] = value;
    this.search_model[field] = value ? this.legalDate.fromPickerDate(value) : null;
    this.syncCalendarSelection();
  }

  resetFilters(): void {
    this.search_model = this.createDefaultSearch();
    if (!this.isOperatorUser) {
      this.search_model.backend_mode = 'event_store';
    }
    this.activeQuickRangeDays = null;
    this.syncPickerValues();
    this.report_error_message = null;
  }

  loadLookups(): void {
    this.lookup_warning_messages = [];

    this.loadArrayLookup('Agent brands / groups', this.demoService.getBrandsComboBox({ brand_type_id: 3 }), (items) => {
      this.brands_agent = items;
    });
    this.loadArrayLookup('Reporting clients', this.demoService.getBrandsComboBox({ brand_type_id: 2 }), (items) => {
      this.brands_client = items;
    });
    this.loadArrayLookup('Job types', this.demoService.getJobTypes(), (items) => {
      this.job_types = items;
    });
    this.loadArrayLookup('Service types', this.demoService.getSubjectServiceTypes(), (items) => {
      this.subject_service_types = items;
    });
    this.loadArrayLookup('Exact outcomes', this.demoService.getServiceOutcomes(), (items) => {
      this.subject_service_outcomes = items;
    });
    this.loadArrayLookup('Resolution outcomes', this.demoService.getSubjectServiceResolutionOutcomes(), (items) => {
      this.subject_service_resolution_outcomes = items;
    });
  }

  loadMetrics(): void {
    const requestModel = this.buildRequestModel();

    this.clearCurrentReportState();
    this.report_error_message = null;
    this.loading = true;
    this.loading_started_at_ms = Date.now();
    this.loading_elapsed_seconds = 0;
    this.startLoadingTimer();
    from(this.resolveExecutionRequestModel(requestModel))
      .pipe(
        concatMap((executionRequestModel) => {
          if (!executionRequestModel) {
            return of(null);
          }

          return this.demoService.getAgentActivityMetricsV2(executionRequestModel);
        }),
        finalize(() => {
          this.loading = false;
          this.stopLoadingTimer();
        })
      )
      .subscribe({
        next: (response) => {
          if (!response) {
            return;
          }

          this.collapsedSections.returned_detail_rows = true;
          this.response = response;
          this.export_preview = null;
          this.report_error_message = null;
          this.hydrateSelectionFromResponse(response);
        },
        error: (error) => {
          this.clearCurrentReportState();
          this.report_error_message = error?.error?.detail || error?.message || 'Failed to load Metrics 2.0.';
        }
      });
  }

  clearCurrentReportState(): void {
    this.response = null;
    this.export_preview = null;
    this.selectedTimelineEvent = null;
    this.selectedSubjectRollup = null;
    this.selectedAuditRecord = null;
    this.selectedMetricKey = null;
    this.selectedAttemptId = null;
    this.selectedSubjectId = null;
    this.activeView = 'all';
  }

  startLoadingTimer(): void {
    this.stopLoadingTimer();
    this.loading_timer = setInterval(() => {
      if (!this.loading_started_at_ms) {
        this.loading_elapsed_seconds = 0;
        return;
      }

      this.loading_elapsed_seconds = Math.max(0, Math.floor((Date.now() - this.loading_started_at_ms) / 1000));
    }, 1000);
  }

  stopLoadingTimer(): void {
    if (this.loading_timer) {
      clearInterval(this.loading_timer);
      this.loading_timer = null;
    }
  }

  get loadingStatusMessage(): string {
    const sourceCount = this.search_model.activity_sources?.length ?? 0;
    const daySpan = this.estimateWindowDays();
    return `Fetching and collating metric intelligence across ${sourceCount} source${sourceCount === 1 ? '' : 's'} for roughly ${daySpan} day${daySpan === 1 ? '' : 's'}.`;
  }

  get loadingStatusDetail(): string {
    const resolvedMode = this.backendDecision?.resolved_backend_mode;
    const resolvedLabel = resolvedMode ? this.formatBackendMode(resolvedMode) : 'Pending';
    return `${this.formatBackendMode(this.search_model.backend_mode)} requested | ${resolvedLabel} resolved | Timeline limit ${this.search_model.limit} | Projection ${this.search_model.projection?.toUpperCase?.() || 'UI'} | ${this.isBroadUiLightweightRequest(this.buildRequestModel()) ? 'Lightweight UI payload' : 'Full audit payload'}`;
  }

  estimateWindowDays(): number {
    if (!this.search_model.when_from || !this.search_model.when_to) {
      return 0;
    }

    const fromTime = new Date(this.search_model.when_from).getTime();
    const toTime = new Date(this.search_model.when_to).getTime();
    if (!Number.isFinite(fromTime) || !Number.isFinite(toTime) || toTime < fromTime) {
      return 0;
    }

    return Math.max(1, Math.ceil((toTime - fromTime) / 86400000));
  }

  exportMetrics(): void {
    const exportRequest: ActivityMetricsV2Search = {
      ...this.buildRequestModel(),
      projection: 'audit',
    };

    this.is_exporting = true;
    from(this.resolveExecutionRequestModel(exportRequest))
      .pipe(
        concatMap((executionRequestModel) => {
          if (!executionRequestModel) {
            return of(null);
          }

          return this.demoService.exportAgentActivityMetricsV2(executionRequestModel);
        }),
        finalize(() => {
          this.is_exporting = false;
        })
      )
      .subscribe((response) => {
        if (!response) {
          return;
        }

        this.export_preview = response;
        this.downloadExportFile(response);
      });
  }

  loadStoreStatus(showToast = false, requestModel?: ActivityMetricsV2Search): void {
    this.is_store_status_loading = true;

    let request$ = this.demoService.getAgentActivityMetricsV2StoreStatus(requestModel ?? this.buildRequestModel());
    if (showToast) {
      request$ = request$.pipe(
        this.toastr.observe({
          loading: 'Loading reporting-table status...',
          success: 'Reporting-table status loaded',
          error: 'Reporting-table status failed',
        }),
      );
    }

    request$
      .pipe(finalize(() => {
        this.is_store_status_loading = false;
      }))
      .subscribe({
        next: (status) => {
          this.store_status = status;
          this.store_error_message = null;
        },
        error: (error) => {
          this.store_error_message = error?.error?.detail || error?.message || 'Failed to load reporting-table status.';
        }
      });
  }

  openHelpModal(template: TemplateRef<any>, helpKey: string): void {
    const helpContent = this.helpContentMap[helpKey];
    if (!helpContent) {
      return;
    }

    this.activeHelpContent = helpContent;
    this.helpModalRef = this.modalService.show(template, {
      class: 'modal-lg',
      ignoreBackdropClick: false,
    });
  }

  closeHelpModal(): void {
    this.helpModalRef?.hide();
    this.helpModalRef = undefined;
    this.activeHelpContent = null;
  }

  openAuditReviewModal(template: TemplateRef<any>): void {
    this.auditReviewModalRef = this.modalService.show(template, {
      class: 'modal-xl',
      ignoreBackdropClick: false,
    });
  }

  closeAuditReviewModal(): void {
    this.auditReviewModalRef?.hide();
    this.auditReviewModalRef = undefined;
  }

  setupStore(): void {
    if (!this.isOperatorUser) {
      return;
    }

    this.is_store_setting_up = true;
    this.demoService.setupAgentActivityMetricsV2Store({})
      .pipe(
        this.toastr.observe({
          loading: 'Seeding reporting-table sources...',
          success: 'Reporting-table setup complete',
          error: 'Reporting-table setup failed'
        }),
        finalize(() => {
          this.is_store_setting_up = false;
        })
      )
      .subscribe({
        next: (response) => {
          this.store_setup_result = response;
          this.store_status = response.status;
          this.store_error_message = null;
        },
        error: (error) => {
          this.store_error_message = error?.error?.detail || error?.message || 'Failed to setup reporting tables.';
        }
      });
  }

  cleanStore(scope: 'range' | 'all'): void {
    if (!this.isOperatorUser) {
      return;
    }

    const requestModel = this.buildRequestModel();
    const cleanupRequestModel = this.buildStoreSyncRequestModel(requestModel);
    const confirmationMessage = scope === 'all'
      ? 'Clean all indexed Metrics 2.0 data and reset coverage state? Raw operational tables and run-history logs will be kept.'
      : 'Clean indexed Metrics 2.0 rows for the selected window and activity sources? Overlapping indexed coverage will be invalidated so the window can be rebuilt cleanly.';

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    this.store_error_message = null;
    this.store_sync_progress_message = scope === 'all'
      ? 'Cleaning all indexed Metrics 2.0 rows and resetting coverage state.'
      : 'Cleaning indexed rows for the selected window and activity sources.';
    this.is_store_cleaning = true;

    this.demoService.cleanupAgentActivityMetricsV2Store({
      ...cleanupRequestModel,
      cleanup_scope: scope,
    })
      .pipe(
        this.toastr.observe({
          loading: scope === 'all' ? 'Cleaning all indexed Metrics 2.0 data...' : 'Cleaning indexed rows for the selected window...',
          success: scope === 'all' ? 'Indexed Metrics 2.0 data cleared' : 'Selected indexed window cleared',
          error: 'Reporting-table cleanup failed'
        }),
        finalize(() => {
          this.is_store_cleaning = false;
        })
      )
      .subscribe({
        next: (response) => {
          this.store_cleanup_result = response;
          this.store_status = response.status;
          this.store_error_message = null;
          this.store_sync_progress_message = scope === 'all'
            ? 'All indexed Metrics 2.0 rows were cleared. Coverage state has been reset.'
            : 'Selected indexed window was cleared. Coverage state has been refreshed.';
        },
        error: (error) => {
          this.store_error_message = error?.error?.detail || error?.message || 'Failed to cleanup reporting tables.';
        }
      });
  }

  syncStore(): void {
    if (!this.isOperatorUser) {
      return;
    }

    this.clearCurrentReportState();
    this.export_preview = null;
    const requestModel = this.buildRequestModel();
    const syncRequestModel = this.buildStoreSyncRequestModel(requestModel);
    const syncRequests = this.buildStoreSyncRequests(syncRequestModel);
    const totalModules = syncRequests.length;
    let latestResponse: ActivityMetricsV2StoreSyncResponse | null = null;
    let aggregatedProcessedCount = 0;
    let aggregatedInsertedCount = 0;
    let aggregatedUpdatedCount = 0;
    let aggregatedSkippedCount = 0;
    let aggregatedElapsedMs = 0;
    let aggregatedSourceCounts: ActivityMetricsV2SourceCount[] = [];
    const aggregatedWarnings = new Set<string>();

    this.store_error_message = null;
    this.store_sync_total_modules = totalModules;
    this.store_sync_completed_modules = 0;
    this.store_sync_progress_message = `Preparing ${totalModules} indexing module${totalModules === 1 ? '' : 's'} for the selected window.`;
    this.is_store_syncing = true;

    if (totalModules > 1) {
      this.toastr.info(`Large indexing window split into ${totalModules} smaller modules.`);
    }

    from(syncRequests)
      .pipe(
        concatMap((syncRequest, index) => {
          this.store_sync_progress_message = this.formatStoreSyncProgressMessage(index + 1, totalModules, syncRequest);

          return this.demoService.syncAgentActivityMetricsV2Store(syncRequest).pipe(
            tap((response) => {
              latestResponse = response;
              this.store_sync_completed_modules = index + 1;
              aggregatedProcessedCount += response.sync_result?.processed_count ?? 0;
              aggregatedInsertedCount += response.sync_result?.inserted_count ?? 0;
              aggregatedUpdatedCount += response.sync_result?.updated_count ?? 0;
              aggregatedSkippedCount += response.sync_result?.skipped_count ?? 0;
              aggregatedElapsedMs += response.diagnostics?.total_elapsed_ms ?? 0;
              aggregatedSourceCounts = this.mergeStoreSourceCounts(
                aggregatedSourceCounts,
                response.sync_result?.source_counts ?? []
              );

              (response.diagnostics?.warnings ?? []).forEach((warning) => aggregatedWarnings.add(warning));
              this.store_sync_result = this.buildAggregatedStoreSyncResponse(
                response,
                aggregatedProcessedCount,
                aggregatedInsertedCount,
                aggregatedUpdatedCount,
                aggregatedSkippedCount,
                aggregatedElapsedMs,
                aggregatedSourceCounts,
                Array.from(aggregatedWarnings)
              );
            }),
            map(() => index + 1)
          );
        }),
        finalize(() => {
          this.is_store_syncing = false;
        })
      )
      .subscribe({
        complete: () => {
          if (latestResponse) {
            this.store_sync_progress_message = `Indexed ${totalModules} module${totalModules === 1 ? '' : 's'} for the selected window. Refreshing full-window coverage status.`;
          }
          this.loadStoreStatus(false, requestModel);
          this.toastr.success(totalModules > 1 ? 'Reporting-table sync modules complete' : 'Reporting-table sync complete');
        },
        error: (error) => {
          const baseMessage = error?.error?.detail || error?.message || 'Failed to sync reporting tables.';
          this.store_error_message = `Module ${Math.min(this.store_sync_completed_modules + 1, totalModules)} of ${totalModules} failed. ${baseMessage}`;
          this.store_sync_progress_message = `Stopped after ${this.store_sync_completed_modules} completed module${this.store_sync_completed_modules === 1 ? '' : 's'}.`;
          this.loadStoreStatus(false, requestModel);
          this.toastr.error('Reporting-table sync failed');
        }
      });
  }

  hydrateSelectionFromResponse(response: ActivityMetricsV2Response): void {
    this.selectedMetricKey = response.metric_results?.[0]?.metric_key ?? null;

    if (response.timeline?.length) {
      this.selectTimelineEvent(response.timeline[0]);
      return;
    }

    if (response.subjects?.length) {
      this.selectSubject(response.subjects[0]);
      return;
    }

    this.selectedTimelineEvent = null;
    this.selectedSubjectRollup = null;
    this.selectedAuditRecord = null;
    this.selectedAttemptId = null;
    this.selectedSubjectId = null;
  }

  selectTimelineEvent(event: ActivityMetricsV2Event): void {
    this.selectedTimelineEvent = event;
    this.selectedAttemptId = event.subject_service_id ?? null;
    this.selectedSubjectId = event.subject_id ?? null;
    this.selectedSubjectRollup = this.findSubjectRollup(event.subject_id ?? null);
    this.selectedAuditRecord = this.findAuditRecord(event.subject_id ?? null);
  }

  selectSubject(subject: ActivityMetricsV2SubjectRollup): void {
    this.selectedSubjectRollup = subject;
    this.selectedSubjectId = subject.subject_id ?? null;
    this.selectedAttemptId = subject.latest_subject_service_id ?? null;
    this.selectedAuditRecord = this.findAuditRecord(subject.subject_id ?? null);
    this.selectedTimelineEvent =
      (this.timeline ?? []).find((event) => event.subject_id === subject.subject_id) ?? null;
  }

  selectMetric(metricKey: string): void {
    this.selectedMetricKey = metricKey;
    if (this.activeView === 'summary') {
      this.activeView = 'metrics';
    }
  }

  setActiveView(viewKey: ActivityMetricsV2ViewKey): void {
    this.activeView = viewKey;
  }

  isActiveView(viewKey: ActivityMetricsV2ViewKey): boolean {
    return this.activeView === viewKey;
  }

  shouldShowViewSection(viewKey: Exclude<ActivityMetricsV2ViewKey, 'all'>): boolean {
    return this.activeView === 'all' || this.activeView === viewKey;
  }

  getMetricDefinition(metricKey: string | null): ActivityMetricsV2MetricDefinition | undefined {
    if (!metricKey || !this.response) {
      return undefined;
    }

    return this.response.metric_definitions.find((definition) => definition.metric_key === metricKey);
  }

  getMetricResult(metricKey: string | null): ActivityMetricsV2MetricResult | undefined {
    if (!metricKey || !this.response) {
      return undefined;
    }

    return this.response.metric_results.find((result) => result.metric_key === metricKey);
  }

  getMetricEligibility(metricKey: string | null): ActivityMetricsV2MetricEligibilityRow | undefined {
    if (!metricKey || !this.response?.diagnostics?.metric_eligibility) {
      return undefined;
    }

    return this.response.diagnostics.metric_eligibility.find((metric) => metric.metric_key === metricKey);
  }

  getMetricAudit(metricKey: string | null): ActivityMetricsV2MetricAuditRow | undefined {
    if (!metricKey) {
      return undefined;
    }

    return this.singleSubjectMetricAuditMap[metricKey];
  }

  getMetricContributors(metricKey: string | null): ActivityMetricsV2MetricContributorRow[] {
    if (!metricKey || !this.response?.fixed_point_results?.length) {
      return [];
    }

    return this.response.fixed_point_results
      .map((record) => {
        const metricAudit = (record.metric_audit ?? []).find((row) => row.metric_key === metricKey);
        if (!metricAudit) {
          return null;
        }

        return {
          subject_id: record.subject_id,
          job_id: record.job_id,
          subject_number: record.subject_number,
          subject_name: record.subject_name,
          ...metricAudit,
        } as ActivityMetricsV2MetricContributorRow;
      })
      .filter((row): row is ActivityMetricsV2MetricContributorRow => !!row)
      .sort((left, right) => {
        if (left.eligible !== right.eligible) {
          return left.eligible ? -1 : 1;
        }

        const leftValue = left.value_ms ?? Number.MAX_SAFE_INTEGER;
        const rightValue = right.value_ms ?? Number.MAX_SAFE_INTEGER;
        if (leftValue !== rightValue) {
          return leftValue - rightValue;
        }

        return String(left.subject_number ?? left.subject_id ?? '').localeCompare(String(right.subject_number ?? right.subject_id ?? ''));
      })
      .slice(0, 25);
  }

  getBreakdown(breakdownKey: string): ActivityMetricsV2Breakdown | undefined {
    return this.response?.grouped_breakdowns.find((breakdown) => breakdown.breakdown_key === breakdownKey);
  }

  getTopBreakdownRows(breakdown: ActivityMetricsV2Breakdown | undefined, count = 8): ActivityMetricsV2BreakdownRow[] {
    if (!breakdown?.rows?.length) {
      return [];
    }

    return [...breakdown.rows].sort((left, right) => (right.count ?? 0) - (left.count ?? 0)).slice(0, count);
  }

  formatPercent(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '-';
    }

    return `${(value * 100).toFixed(1)}%`;
  }

  formatMilliseconds(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '-';
    }

    if (value >= 1000) {
      return `${(value / 1000).toFixed(2)}s`;
    }

    return `${Math.round(value)}ms`;
  }

  formatStageLabel(stageKey: string | null | undefined): string {
    if (!stageKey) {
      return '-';
    }

    return stageKey
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  formatBackendMode(mode: ActivityMetricsV2BackendMode | null | undefined): string {
    switch (mode) {
      case 'event_store':
        return 'Reporting Tables';
      case 'live':
        return 'Live Tables';
      case 'auto':
      default:
        return 'Auto';
    }
  }

  formatBooleanFlag(value: boolean | null | undefined): string {
    return value ? 'Yes' : 'No';
  }

  isSectionCollapsed(sectionKey: string): boolean {
    return !!this.collapsedSections[sectionKey];
  }

  toggleSection(sectionKey: string): void {
    this.collapsedSections[sectionKey] = !this.collapsedSections[sectionKey];
  }

  formatDuration(value: number | null | undefined): string {
    if (!value || value < 0) {
      return '-';
    }

    const totalSeconds = Math.floor(value / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    const parts: string[] = [];

    if (days > 0) {
      parts.push(`${days}d`);
    }

    if (hours > 0) {
      parts.push(`${hours}h`);
    }

    if (minutes > 0) {
      parts.push(`${minutes}m`);
    }

    if (!parts.length) {
      parts.push(`${totalSeconds}s`);
    }

    return parts.join(' ');
  }

  parseCsvFilter(value: string | null | undefined): string[] {
    if (!value) {
      return [];
    }

    return value
      .split(',')
      .map((item) => item.trim())
      .filter((item) => !!item);
  }

  getSubjectMetricValue(subject: ActivityMetricsV2SubjectRollup, metricKey: string): string {
    const rawValue = subject.metric_values?.[metricKey];

    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return '-';
    }

    if (typeof rawValue === 'number') {
      return this.formatDuration(rawValue);
    }

    return `${rawValue}`;
  }

  hasSubjectMetricValue(subject: ActivityMetricsV2SubjectRollup, metricKey: string): boolean {
    const rawValue = subject.metric_values?.[metricKey];
    return !(rawValue === null || rawValue === undefined || rawValue === '');
  }

  getSubjectMetricCards(subject: ActivityMetricsV2SubjectRollup): Array<{
    metric_key: string;
    label: string;
    value: string;
    benchmark_text?: string | null;
  }> {
    return (this.response?.metric_results ?? [])
      .filter((metric) => this.hasSubjectMetricValue(subject, metric.metric_key))
      .map((metric) => ({
        metric_key: metric.metric_key,
        label: metric.label,
        value: this.getSubjectMetricValue(subject, metric.metric_key),
        benchmark_text: metric.benchmark_text ?? null,
      }));
  }

  trackByValue(index: number, item: SelectOption): string | number {
    return item.value;
  }

  trackByLabel(index: number, item: { label: string }): string {
    return item.label;
  }

  trackByEvent(index: number, item: ActivityMetricsV2Event): string {
    return item.event_id;
  }

  trackByMetric(index: number, item: ActivityMetricsV2MetricResult): string {
    return item.metric_key;
  }

  trackByBreakdown(index: number, item: ActivityMetricsV2Breakdown): string {
    return item.breakdown_key;
  }

  trackBySourceCount(index: number, item: ActivityMetricsV2StoreEventCount | ActivityMetricsV2SourceCount): string {
    return item.source_key;
  }

  trackBySyncRun(index: number, item: ActivityMetricsV2StoreSyncRun): number {
    return item.activity_metrics_v2_sync_run_id;
  }

  trackBySyncState(index: number, item: ActivityMetricsV2StoreSyncState): string {
    return item.source_key;
  }

  trackBySourceKey(index: number, item: string): string {
    return item;
  }

  trackByReportRunStatus(index: number, item: ActivityMetricsV2StoreReportRunStatusCount): string {
    return item.status;
  }

  private loadArrayLookup<T>(label: string, request$: Observable<T>, assign: (items: any[]) => void): void {
    request$
      .pipe(
        catchError((error) => {
          const detail = error?.error?.detail || error?.message || 'Lookup request failed.';
          this.lookup_warning_messages = [...this.lookup_warning_messages, `${label}: ${detail}`];
          assign([]);
          return of([]);
        })
      )
      .subscribe((result) => {
        assign(Array.isArray(result) ? result : []);
      });
  }

  private syncCalendarSelection(): void {
    const whenFrom = this.pickerValues.when_from;
    const whenTo = this.pickerValues.when_to;

    if (!whenFrom || !whenTo) {
      this.activeMonthIndex = null;
      this.activeDayOfMonth = null;
      return;
    }

    this.selectedCalendarYear = whenFrom.getFullYear();

    const isSameMonth = whenFrom.getFullYear() === whenTo.getFullYear()
      && whenFrom.getMonth() === whenTo.getMonth();
    const isMonthStart = whenFrom.getDate() === 1
      && whenFrom.getHours() === 0
      && whenFrom.getMinutes() === 0;
    const expectedMonthEnd = new Date(whenFrom.getFullYear(), whenFrom.getMonth() + 1, 0, 23, 59, 0, 0);
    const isMonthEnd = whenTo.getFullYear() === expectedMonthEnd.getFullYear()
      && whenTo.getMonth() === expectedMonthEnd.getMonth()
      && whenTo.getDate() === expectedMonthEnd.getDate()
      && whenTo.getHours() === expectedMonthEnd.getHours()
      && whenTo.getMinutes() === expectedMonthEnd.getMinutes();

    const isSameDay = isSameMonth
      && whenFrom.getDate() === whenTo.getDate();
    const isDayStart = whenFrom.getHours() === 0
      && whenFrom.getMinutes() === 0;
    const isDayEnd = whenTo.getHours() === 23
      && whenTo.getMinutes() === 59;

    this.activeMonthIndex = isSameMonth && isMonthStart && isMonthEnd
      ? whenFrom.getMonth()
      : null;
    this.activeDayOfMonth = isSameDay && isDayStart && isDayEnd
      ? whenFrom.getDate()
      : null;
  }

  private findSubjectRollup(subjectId: number | null): ActivityMetricsV2SubjectRollup | null {
    if (subjectId === null || subjectId === undefined) {
      return null;
    }

    return this.response?.subjects.find((subject) => subject.subject_id === subjectId) ?? null;
  }

  private findAuditRecord(subjectId: number | null): ActivityMetricsV2FixedPointAudit | null {
    if (subjectId === null || subjectId === undefined) {
      return null;
    }

    return this.response?.fixed_point_results.find((record) => record.subject_id === subjectId) ?? null;
  }

  private buildStoreSyncRequests(requestModel: ActivityMetricsV2Search): ActivityMetricsV2Search[] {
    const whenFromMs = requestModel.when_from ? Date.parse(requestModel.when_from) : Number.NaN;
    const whenToMs = requestModel.when_to ? Date.parse(requestModel.when_to) : Number.NaN;
    const moduleDays = this.getStoreSyncModuleDays(requestModel);

    if (!Number.isFinite(whenFromMs) || !Number.isFinite(whenToMs) || moduleDays <= 0) {
      return [requestModel];
    }

    const requests: ActivityMetricsV2Search[] = [];
    const moduleDurationMs = moduleDays * 86400000;
    let cursorMs = whenFromMs;

    while (cursorMs <= whenToMs) {
      const windowEndMs = Math.min(whenToMs, cursorMs + moduleDurationMs);

      requests.push({
        ...requestModel,
        when_from: new Date(cursorMs).toISOString(),
        when_to: new Date(windowEndMs).toISOString(),
      });

      if (windowEndMs >= whenToMs) {
        break;
      }

      // Keep module boundaries aligned to the same UTC-second model as the backend.
      cursorMs = windowEndMs + 1000;
    }

    return requests.length ? requests : [requestModel];
  }

  private buildStoreSyncRequestModel(requestModel: ActivityMetricsV2Search): ActivityMetricsV2Search {
    return {
      ...requestModel,
      brand_id_group: [],
      brand_report_id_group: [],
      subject_number_group: [],
      subject_number: null,
      subject_id: null,
      job_id: null,
      job_type_id_group: [],
      subject_service_type_id_group: [],
      subject_service_resolution_outcome_id_group: [],
      subject_service_outcome_id_group: [],
      state_group: [],
      postcode_group: [],
      suburb_group: [],
    };
  }

  private getStoreSyncModuleDays(requestModel: ActivityMetricsV2Search): number {
    const windowDays = this.getSearchWindowDays(requestModel.when_from, requestModel.when_to);
    const includesSystemLog = (requestModel.activity_sources ?? []).includes('system_log');

    if (windowDays <= 7) {
      return Math.max(1, windowDays);
    }

    if (includesSystemLog) {
      if (windowDays >= 90) {
        return 2;
      }
      if (windowDays >= 31) {
        return 3;
      }

      return 4;
    }

    if (windowDays >= 180) {
      return 7;
    }
    if (windowDays >= 90) {
      return 5;
    }

    return 4;
  }

  private getSearchWindowDays(whenFrom: string | null | undefined, whenTo: string | null | undefined): number {
    if (!whenFrom || !whenTo) {
      return 0;
    }

    const fromDate = new Date(whenFrom);
    const toDate = new Date(whenTo);

    if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
      return 0;
    }

    const diffMs = Math.max(0, toDate.getTime() - fromDate.getTime());
    return Math.max(1, Math.ceil(diffMs / 86400000));
  }

  private formatStoreSyncProgressMessage(currentModule: number, totalModules: number, requestModel: ActivityMetricsV2Search): string {
    const whenFromText = requestModel.when_from
      ? this.legalDate.formatAudit(requestModel.when_from, 'DD/MM/YYYY h:mm a')
      : '-';
    const whenToText = requestModel.when_to
      ? this.legalDate.formatAudit(requestModel.when_to, 'DD/MM/YYYY h:mm a')
      : '-';

    return `Syncing module ${currentModule} of ${totalModules}: ${whenFromText} to ${whenToText}.`;
  }

  private mergeStoreSourceCounts(
    existingCounts: ActivityMetricsV2SourceCount[],
    incomingCounts: ActivityMetricsV2SourceCount[]
  ): ActivityMetricsV2SourceCount[] {
    const merged = new Map<string, ActivityMetricsV2SourceCount>();

    existingCounts.forEach((item) => {
      merged.set(item.source_key, { ...item });
    });

    incomingCounts.forEach((item) => {
      const current = merged.get(item.source_key);
      if (!current) {
        merged.set(item.source_key, { ...item });
        return;
      }

      current.count += item.count ?? 0;
    });

    return Array.from(merged.values()).sort((left, right) => {
      if ((right.count ?? 0) === (left.count ?? 0)) {
        return left.source_key.localeCompare(right.source_key);
      }

      return (right.count ?? 0) - (left.count ?? 0);
    });
  }

  private buildAggregatedStoreSyncResponse(
    response: ActivityMetricsV2StoreSyncResponse,
    processedCount: number,
    insertedCount: number,
    updatedCount: number,
    skippedCount: number,
    elapsedMs: number,
    sourceCounts: ActivityMetricsV2SourceCount[],
    warnings: string[]
  ): ActivityMetricsV2StoreSyncResponse {
    return {
      ...response,
      sync_result: {
        ...response.sync_result,
        processed_count: processedCount,
        inserted_count: insertedCount,
        updated_count: updatedCount,
        skipped_count: skippedCount,
        source_counts: sourceCounts,
      },
      diagnostics: {
        ...response.diagnostics,
        total_elapsed_ms: elapsedMs,
        warnings,
      }
    };
  }

  private downloadExportFile(response: ActivityMetricsV2ExportResponse): void {
    const fileName = response.profile?.file_name
      || `activity-metrics-v2-${response.profile?.key || 'export'}.json`;

    const blob = new Blob([JSON.stringify(response, null, 2)], {
      type: 'application/json;charset=utf-8'
    });

    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  }

  private buildRequestModel(): ActivityMetricsV2Search {
    const backendMode: ActivityMetricsV2BackendMode = this.isOperatorUser
      ? (this.search_model.backend_mode ?? 'event_store')
      : 'event_store';

    const requestModel: ActivityMetricsV2Search = {
      ...this.search_model,
      when_from: this.search_model.when_from ?? this.legalDate.fromPickerDate(this.pickerValues.when_from),
      when_to: this.search_model.when_to ?? this.legalDate.fromPickerDate(this.pickerValues.when_to),
      backend_mode: backendMode,
      activity_sources: this.search_model.activity_sources?.length
        ? [...this.search_model.activity_sources]
        : [...this.defaultSources],
      group_by: this.search_model.group_by?.length
        ? [...this.search_model.group_by]
        : [...this.defaultGroupBy],
    };

    requestModel.classic_lightweight = this.isBroadUiLightweightRequest(requestModel);

    return requestModel;
  }

  private isBroadUiLightweightRequest(requestModel: ActivityMetricsV2Search): boolean {
    if ((requestModel.projection ?? 'ui') !== 'ui') {
      return false;
    }

    if (requestModel.include_breakdowns) {
      return false;
    }

    if (requestModel.hosted_context_panel) {
      return false;
    }

    const hasExactDrilldown = !!requestModel.subject_id
      || !!requestModel.job_id
      || !!(requestModel.subject_number && String(requestModel.subject_number).trim().length)
      || !!(requestModel.subject_number_group?.length);

    return !hasExactDrilldown;
  }

  private buildCoveragePresentation(
    requestModel: ActivityMetricsV2Search,
    status: ActivityMetricsV2StoreStatus | null,
  ): ActivityMetricsV2CoveragePresentation {
    return buildActivityMetricsV2CoveragePresentation({
      status,
      requestedSources: requestModel.activity_sources ?? this.search_model.activity_sources,
      requestedWhenFrom: requestModel.when_from,
      requestedWhenTo: requestModel.when_to,
      formatDate: (value: string) => this.legalDate.formatAudit(value, 'DD/MM/YYYY h:mm a'),
      formatDuration: (value: number) => this.formatDuration(value),
    });
  }

  private async fetchStoreStatus(requestModel: ActivityMetricsV2Search): Promise<ActivityMetricsV2StoreStatus | null> {
    this.is_store_status_loading = true;

    try {
      const status = await firstValueFrom(this.demoService.getAgentActivityMetricsV2StoreStatus(requestModel));
      this.store_status = status ?? null;
      this.store_error_message = null;
      return status ?? null;
    } catch (error: any) {
      this.store_error_message = error?.error?.detail || error?.message || 'Failed to load reporting-table status.';
      return null;
    } finally {
      this.is_store_status_loading = false;
    }
  }

  private async resolveExecutionRequestModel(requestModel: ActivityMetricsV2Search): Promise<ActivityMetricsV2Search | null> {
    if (requestModel.backend_mode !== 'event_store') {
      return requestModel;
    }

    const status = await this.fetchStoreStatus(requestModel);
    const coveragePresentation = this.buildCoveragePresentation(requestModel, status);
    if (
      !coveragePresentation.hasIndexedCoverage
      || !coveragePresentation.effectiveWindowFromUtc
      || !coveragePresentation.effectiveWindowToUtc
    ) {
      this.report_error_message = coveragePresentation.detail;
      return null;
    }

    return applyActivityMetricsV2IndexedWindowToRequest(requestModel, coveragePresentation);
  }

  private enrichJobLookupItem(item: any): any {
    const jobId = item?.job_id ?? null;
    const jobNumber = item?.job_number ?? item?.job_reference_number ?? null;
    const clientReference = item?.client_reference ?? item?.client_referernce ?? item?.reference_number ?? null;
    const reportingClient = item?.brand_report_name ?? item?.brand_invoice_name ?? null;
    const subjectNumber = item?.subject_number ?? null;
    const subjectName = item?.subject_name ?? item?.business_name ?? this.buildPersonName(item);

    const jobParts = [
      jobId ? `Job ${jobId}` : null,
      jobNumber ? `#${jobNumber}` : null,
      clientReference ? `Ref ${clientReference}` : null,
      reportingClient ? reportingClient : null
    ].filter(Boolean);

    const subjectParts = [
      subjectNumber ? `Subject ${subjectNumber}` : null,
      subjectName || null
    ].filter(Boolean);

    return {
      ...item,
      metrics_job_label: jobParts.join(' | ') || String(jobId ?? ''),
      metrics_subject_label: [...jobParts, ...subjectParts].join(' | ') || String(subjectNumber ?? jobId ?? '')
    };
  }

  private buildPersonName(item: any): string | null {
    const parts = [
      item?.first_name,
      item?.middle_name,
      item?.surname
    ].filter((value: unknown) => typeof value === 'string' && value.trim().length);

    return parts.length ? parts.join(' ') : null;
  }
}
